import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Info, ChevronRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Header } from '@/components/layout/Header';
import { WelcomeHero } from '@/components/home/WelcomeHero';
import { InfoDialog, HOW_APP_WORKS_STEPS } from '@/components/info/InfoDialog';
import { IntercomInbox } from '@/components/intercom/IntercomInbox';
import { InvitationCard } from '@/components/devices/InvitationCard';
import { KeyAccessList } from '@/components/keys/KeyAccessList';
import { NearbyKeyReady } from '@/components/keys/NearbyKeyReady';
import { useDevices } from '@/hooks/useDevices';
import { useAllKeys } from '@/hooks/useKeys';
import { useInvitations, useAcceptInvitation, useDeclineInvitation } from '@/hooks/useInvitations';
import { useDeviceStore } from '@/stores/deviceStore';
import { groupByFacility } from '@/lib/facilities';

/**
 * HomePage — appens välkomst-/landningssida (fliken "Hem").
 *
 * Behåller den tidigare gate-logiken: oautentiserade skickas till /auth
 * och väntande intercom-deep-links (från SMS) respekteras. För inloggade
 * användare:
 *   1) WelcomeHero — metallisk smart lock-hero med logo + hälsning (bildyta)
 *   2) Inkorg — porttelefon (IntercomInbox) och nya inbjudningar
 *   3) Snabbstatistik
 *   4) Mina nycklar — alla nycklar direkt på Hem (samma lista som Nycklar,
 *      utan anläggningsmeny/hantering); tap öppnar nyckeln
 *   5) Info-ruta — "Så fungerar appen" öppnas i dialog istället för att
 *      instruktionen ligger fast på sidan
 *
 * Snabblänkarna togs bort — destinationerna finns redan i bottenmenyn.
 */
export default function HomePage() {
  const navigate = useNavigate();
  const { user, loading, profile } = useAuth();

  // Håll enhets-storen varm och hämta underlag till statistiken.
  const { assignments } = useDeviceStore();
  useDevices();
  const { data: keys } = useAllKeys();
  const { data: invitations } = useInvitations();
  const acceptInvitation = useAcceptInvitation();
  const declineInvitation = useDeclineInvitation();

  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [decliningId, setDecliningId] = useState<string | null>(null);

  useEffect(() => {
    if (loading) return;
    if (user) {
      // Väntande intercom-redirect (från SMS deep link) går före startsidan.
      const intercomRedirect = sessionStorage.getItem('intercom_redirect');
      if (intercomRedirect) {
        sessionStorage.removeItem('intercom_redirect');
        navigate(intercomRedirect, { replace: true });
      }
    } else {
      navigate('/auth', { replace: true });
    }
  }, [user, loading, navigate]);

  const stats = useMemo(() => {
    const facilities = groupByFacility(assignments).length;
    return [
      { label: 'Anläggningar', value: facilities },
      { label: 'Enheter', value: assignments.length },
      { label: 'Aktiva nycklar', value: keys?.length ?? 0 },
    ];
  }, [assignments, keys]);

  const handleAccept = async (id: string) => {
    setAcceptingId(id);
    try {
      await acceptInvitation.mutateAsync(id);
    } finally {
      setTimeout(() => setAcceptingId(null), 350);
    }
  };

  const handleDecline = async (id: string) => {
    setDecliningId(id);
    try {
      await declineInvitation.mutateAsync(id);
    } finally {
      setTimeout(() => setDecliningId(null), 400);
    }
  };

  // Väntar på auth eller på väg till /auth — visa en lugn spinner.
  if (loading || !user) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </main>
    );
  }

  const firstName = profile?.display_name?.split(' ')[0];
  const hasInvitations = !!invitations && invitations.length > 0;

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 space-y-6 px-4 py-6">
        <WelcomeHero firstName={firstName} />

        {/* Nyckel redo — geofence-träffar när funktionen är aktiverad */}
        <NearbyKeyReady />

        {/* Inkorg: porttelefon + nya inbjudningar */}
        <IntercomInbox />

        {hasInvitations && (
          <section className="space-y-3 animate-fade-in">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-primary" />
              <h2 className="text-base font-semibold">Nya inbjudningar</h2>
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground">
                {invitations!.length}
              </span>
            </div>
            <div className="space-y-3">
              {invitations!.map((inv) => (
                <InvitationCard
                  key={inv.id}
                  invitation={inv}
                  onAccept={handleAccept}
                  onDecline={handleDecline}
                  isAccepting={acceptingId === inv.id}
                  isDeclining={decliningId === inv.id}
                />
              ))}
            </div>
          </section>
        )}

        {/* Snabbstatistik */}
        <section className="grid grid-cols-3 gap-2.5 animate-fade-in">
          {stats.map((s) => (
            <div key={s.label} className="rounded-2xl glass-thin px-3 py-3 text-center">
              <p className="text-2xl font-semibold tracking-tight">{s.value}</p>
              <p className="mt-0.5 text-[11px] leading-tight text-muted-foreground">
                {s.label}
              </p>
            </div>
          ))}
        </section>

        {/* Mina nycklar — alla nycklar direkt på Hem; tap öppnar nyckeln */}
        <section className="space-y-3 animate-fade-in">
          <h2 className="text-base font-semibold">Mina nycklar</h2>
          <KeyAccessList onAdd={() => navigate('/keys')} />
        </section>

        {/* Info-ruta: instruktionen visas i dialog istället för fast på sidan */}
        <InfoDialog title="Så fungerar appen" steps={HOW_APP_WORKS_STEPS}>
          <button className="flex w-full items-center gap-3 rounded-2xl glass-thin p-4 text-left transition-all animate-fade-in active:scale-[0.98]">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[hsl(var(--foreground)/0.08)] ring-1 ring-[hsl(var(--glass-stroke))]">
              <Info className="h-5 w-5 text-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold leading-tight">Så fungerar appen</p>
              <p className="mt-0.5 text-sm text-muted-foreground">Kom igång på tre steg</p>
            </div>
            <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
          </button>
        </InfoDialog>
      </main>
    </div>
  );
}
